import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';
import { type AbiItem } from 'web3-utils';

import { apiFactory } from '@app/client/apiFactory/api';
import { Events } from '@app/client/common/structs/events/events';
import { ArtifactMintSteps, ArtifactType } from '@app/client/common/structs/nft/artifacts';
import { Artifacts } from '@app/client/resources/nft';
import config from '@app/config';
import { prefetch2FA } from '@app/helpers';
import { setArtifactMintPanelStep } from '@app/store/actions/artifacts';
import { registerEvent } from '@app/store/actions/events';
import { setOpenSeaUrl, watchAsset } from '@app/store/actions/nft';
import { type RootState } from '@app/store/reducers';
import { type ArtifactTokenId, type LootBoxId, type LootBoxTokenIdMapping } from '@app/store/types/artifacts';
import { type ArtifactsAction, SET_LOOT_BOX_TOKEN_ID_MAPPING } from '@app/store/types/artifacts-actions';

const { nftOpenseaLink } = config;

export function setLootBoxTokenIdMapping(lootboxTokenIdMapping: LootBoxTokenIdMapping): ArtifactsAction {
    return {
        type: SET_LOOT_BOX_TOKEN_ID_MAPPING,
        lootboxTokenIdMapping,
    };
}

export function mintArtifact(artifactType: ArtifactType, artifactId: string, tokenContractAddress: string) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        if (artifactId && window.ethereum) {
            localStorage.setItem('artifactMintingAttempted', '1'); // because the 2fa auth calls initializeUser which reset the panel state
            try {
                // Start minting process
                const from = window.ethereum.selectedAddress;
                const web3module = await import('web3');
                const Web3 = web3module.default;
                const web3 = new Web3(window.ethereum);

                const module = await import('@pkg/assets/contracts/YAT.json');
                const YAT = module.default;
                const YatContract = new web3.eth.Contract(YAT.abi as AbiItem[], tokenContractAddress);
                const signingResult = await apiFactory().call(Artifacts.generateSignature, {
                    prefetch: prefetch2FA(dispatch, true, () =>
                        dispatch(setArtifactMintPanelStep(ArtifactMintSteps.Loading)),
                    ),
                    value: { account: from },
                    artifact_type: artifactType,
                    artifact_id: artifactId,
                });

                if (signingResult.ok) {
                    const token = artifactId;
                    const expiry = signingResult.value.expiry;
                    const signature = signingResult.value.signature;
                    const result = await YatContract.methods.mint(token, from, expiry, signature).send({ from });
                    if (result.status) {
                        localStorage.removeItem('artifactMintingInitiated');
                        dispatch(await watchAsset(tokenContractAddress));
                        dispatch(setArtifactMintPanelStep(ArtifactMintSteps.ArtifactMintedFeedback));
                        registerEvent(Events.artifact_minted);

                        // Set OpenSea URL for the confirmation screen
                        const userBalance = await YatContract.methods.balanceOf(from).call({ from });
                        if (userBalance > 0) {
                            const lastTokenId: ArtifactTokenId = await YatContract.methods
                                .tokenOfOwnerByIndex(from, userBalance - 1)
                                .call({ from });
                            const url = `${nftOpenseaLink}/0x${tokenContractAddress}/${lastTokenId}`;
                            dispatch(setOpenSeaUrl(url));
                            if (artifactType !== ArtifactType.Yat) {
                                dispatch(setLootBoxTokenIdMapping({ [token as LootBoxId]: lastTokenId }));
                            }
                        }
                        setTimeout(function () {
                            dispatch(setArtifactMintPanelStep(ArtifactMintSteps.Complete));
                        }, 2 * 1000); // 2 seconds
                    } else {
                        dispatch(setArtifactMintPanelStep(ArtifactMintSteps.Error));
                    }
                } else {
                    localStorage.removeItem('artifactMintingInitiated');
                    dispatch(setArtifactMintPanelStep(ArtifactMintSteps.Error));
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (error: any) {
                // User rejected signature on MetaMask
                if (error && error.code && error.code === 4001) {
                    dispatch(setArtifactMintPanelStep(ArtifactMintSteps.AcceptTerms));
                    localStorage.removeItem('artifactMintingInitiated');
                } else {
                    dispatch(setArtifactMintPanelStep(ArtifactMintSteps.Error));
                    localStorage.removeItem('artifactMintingInitiated');
                }
            }
        }
    };
}
